
import React from 'react';
import { BLOGS } from '../constants';
import { Calendar, Tag, ChevronRight, BookOpen, Clock, User, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Blogs: React.FC = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-16 animate-in fade-in slide-in-from-bottom-4 duration-1000">
      {/* Header Section */}
      <div className="text-center mb-24 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-12 opacity-5 pointer-events-none">
          <BookOpen size={240} className="text-blue-500" />
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-400 text-[10px] font-black uppercase tracking-[0.3em] mb-8 relative z-10">
          <Sparkles size={14} className="text-yellow-500" />
          Aspirant's Knowledge Vault 2026
        </div>
        <h1 className="text-5xl md:text-8xl font-black mb-8 uppercase tracking-tighter italic leading-none text-white relative z-10">
          SUCCESS <span className="text-blue-500 not-italic">HUB</span>
        </h1>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto font-medium leading-relaxed relative z-10">
          Helping 2026 aspirants optimize their prep and savings with verified expert data from <span className="text-white font-bold italic">Jitesh Jana</span> and the ambassador community.
        </p>
      </div>

      {/* Featured Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-32">
        {BLOGS.map((blog, idx) => (
          <article 
            key={blog.id} 
            className={`bg-[#0f0f0f] border border-white/5 rounded-[40px] overflow-hidden hover:border-blue-500/40 transition-all group flex flex-col h-full shadow-2xl relative ${idx === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
          >
            {/* Visual Header */}
            <div className="aspect-[16/9] bg-[#111] overflow-hidden relative">
              <img 
                src={`https://picsum.photos/seed/${blog.id}/800/450`} 
                alt={blog.title} 
                loading="lazy"
                className="w-full h-full object-cover opacity-40 grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-1000" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-transparent to-transparent opacity-90" />
              <div className="absolute top-6 left-6 flex items-center gap-2">
                 <span className="bg-blue-600/80 backdrop-blur-md px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest text-white shadow-xl border border-white/10">{blog.category}</span>
              </div>
            </div>

            {/* Content Body */}
            <div className="p-10 flex flex-col flex-grow">
              <div className="flex items-center gap-5 text-[9px] text-gray-500 font-black uppercase tracking-widest mb-6">
                <span className="flex items-center gap-1.5"><Calendar size={12} className="text-blue-500" /> {blog.date}</span>
                <span className="flex items-center gap-1.5"><Clock size={12} className="text-gray-700" /> 5 MIN READ</span>
              </div>
              
              <h2 className="text-2xl font-black mb-6 group-hover:text-blue-400 transition-colors leading-[1.1] text-white uppercase tracking-tighter italic">
                {blog.title}
              </h2>
              
              <p className="text-gray-500 text-sm mb-10 line-clamp-3 font-medium italic leading-relaxed">
                {blog.excerpt}
              </p>

              {/* Read Full Article Button - SEO Enhanced */}
              <div className="mt-auto pt-8 border-t border-white/5 flex items-center justify-between">
                <Link 
                  to={`/blogs/${blog.id}`} 
                  className="inline-flex items-center gap-2 bg-blue-600/10 hover:bg-blue-600 border border-blue-600/20 hover:border-blue-600 text-blue-400 hover:text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all group/btn"
                >
                  Read Full Article <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                </Link>
                <div className="flex items-center gap-3">
                   <div className="w-8 h-8 rounded-full bg-blue-600/20 border border-blue-600/30 flex items-center justify-center">
                     <User size={14} className="text-blue-500" />
                   </div>
                   <span className="text-[9px] font-black text-white uppercase tracking-widest">J. Jana</span>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Community Newsletter Section */}
      <div className="bg-[#111] border border-white/5 rounded-[60px] p-12 md:p-24 text-center relative overflow-hidden group mb-32">
        <div className="absolute inset-0 bg-blue-600/5 blur-[120px] rounded-full group-hover:scale-125 transition-transform duration-1000"></div>
        <div className="relative z-10">
          <h3 className="text-4xl font-black text-white uppercase tracking-tighter mb-6 italic">Join the Elite Aspirants List</h3>
          <p className="text-gray-500 font-bold uppercase tracking-tight mb-12 max-w-xl mx-auto">Get exclusive price leaks and batch alerts directly from <span className="text-blue-500">Jitesh Jana's</span> network.</p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" onSubmit={(e) => e.preventDefault()}>
            <input type="email" placeholder="ENTER YOUR STUDY EMAIL" className="flex-grow bg-white/5 border border-white/10 rounded-2xl px-6 py-4 font-black text-xs uppercase tracking-widest focus:outline-none focus:border-blue-500 transition-all" />
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all shadow-xl shadow-blue-600/20 active:scale-95">Subscribe</button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/5 pt-12 pb-8">
        <p className="text-[10px] text-gray-700 leading-relaxed text-center font-black uppercase tracking-[0.2em]">
          eduoffers.tech is an independent portal managed by Jitesh Jana and the student ambassador network.
        </p>
      </div>
    </div>
  );
};

export default Blogs;
